// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========

import { MorphIcon } from '@/components/ui/morph-icon';
import { cn } from '@/lib/utils';
import { ChevronRight, ChevronUp } from 'lucide';
import type { ReactNode } from 'react';

export function DisclosureTrigger({
  expanded,
  onExpandedChange,
  children,
  className,
  ariaLabel,
  controlsId,
  expandedChevron = 'up',
  chevronPlacement = 'end',
}: {
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  children: ReactNode;
  className?: string;
  ariaLabel?: string;
  controlsId?: string;
  expandedChevron?: 'up' | 'down';
  chevronPlacement?: 'inline' | 'end';
}) {
  return (
    <button
      type="button"
      aria-expanded={expanded}
      aria-label={ariaLabel}
      aria-controls={controlsId}
      data-chevron-placement={chevronPlacement}
      className={cn(
        'flex w-full items-center gap-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ds-ring-brand-default-focus',
        className
      )}
      onClick={() => onExpandedChange(!expanded)}
    >
      <span
        className={cn(
          'min-w-0',
          chevronPlacement === 'end' ? 'flex-1' : 'shrink'
        )}
      >
        {children}
      </span>
      <MorphIcon
        icon={expanded ? ChevronUp : ChevronRight}
        className={cn(
          'h-4 w-4 shrink-0 text-ds-icon-neutral-muted-default',
          expanded && expandedChevron === 'down' && 'rotate-180'
        )}
        aria-hidden
      />
    </button>
  );
}
